import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { RecordCard, RecordCardList } from '../../primitives';
import {
  Table,
  type BulkAction,
  type CellValue,
  type Column,
  type Row as TableRow,
  type TableUrlStateAdapter
} from '../../Table';
import { applyWorkItemFilters } from '../applyWorkItemFilters';
import type { GroupBy, WorkItemFilters } from '../types';
import { BOARD_CHIP_KEYS } from '../../../pages/work-items/board/types';
import { chipValueFor } from '../../../pages/work-items/board/chipUtils';
import { WorkItemCardChips } from '../../../pages/work-items/board/WorkItemCardChips';
import {
  PRIORITY_CHOICES,
  STATUS_CHOICES,
  TABLE_DEFAULT_SORT,
  WORK_ITEM_COLUMNS,
  type WorkItemRow
} from './tableColumns';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Fetches the work-item rows + org member roster. Org-scoped by default; when
 * `projectId` is set, additionally scopes to a single project (mirrors
 * WorkItemsBoardTab so the Project detail Table shows only that project's items).
 */
function useWorkItems(
  activeOrgId: string | null,
  projectId?: string
): {
  state: LoadState;
  rows: ReadonlyArray<WorkItemRow>;
  profilesById: ReadonlyMap<string, string>;
  error: string | null;
  reload: () => void;
  setRows: (next: ReadonlyArray<WorkItemRow>) => void;
} {
  const [rows, setRows] = useState<ReadonlyArray<WorkItemRow>>([]);
  const [profilesById, setProfilesById] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Until we know which org to scope the member roster to, stay in 'loading'
    // — otherwise the table flashes 'ready' with an empty profilesById, the
    // "Created by" column renders as "—" for one frame, and inline-edit specs
    // that race the org-resolve effect against the data-state assertion can
    // catch the table mid-resolve.
    if (!activeOrgId) {
      setState('loading');
      return;
    }
    setState('loading');
    setError(null);
    void (async () => {
      // Fire the work-items fetch + member roster in parallel — both depend
      // only on activeOrgId, so there's no reason to sequence them.
      let itemsQuery = supabase
        .from('work_items')
        .select(
          'id, name, type, status, rank, parent_work_item_id, start_date, end_date, due_date, created_at, created_by, updated_at, assignee_id, priority'
        )
        .eq('org_id', activeOrgId);
      // Scope to a single project when `projectId` is set (Project detail Table);
      // otherwise stay org-wide (Work Items page Table).
      if (projectId) itemsQuery = itemsQuery.eq('project_id', projectId);
      const [workItemsRes, membersRes] = await Promise.all([
        itemsQuery.order('name', { ascending: true }),
        supabase.rpc('list_org_members', { p_org_id: activeOrgId })
      ]);
      if (cancelled) return;
      if (workItemsRes.error) {
        setError(workItemsRes.error.message);
        setState('error');
        return;
      }
      const fetched = (workItemsRes.data ?? []) as WorkItemRow[];
      setRows(fetched);

      // Resolve user_id → display_name via the org's roster (same canonical
      // path Documents uses). Powers both the "Created by" column and the
      // Assignee dropdown options + cell rendering. RLS-hidden / deleted
      // users fall through to the "—" placeholder in toCellRow.
      if (!membersRes.error && Array.isArray(membersRes.data)) {
        const map = new Map<string, string>();
        for (const m of membersRes.data as ReadonlyArray<{
          user_id?: string | null;
          display_name?: string | null;
          username?: string | null;
        }>) {
          if (!m.user_id) continue;
          const name =
            m.display_name?.trim() || m.username?.trim() || '';
          if (name) map.set(m.user_id, name);
        }
        setProfilesById(map);
      }
      setState('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [tick, activeOrgId, projectId]);

  return {
    state,
    rows,
    profilesById,
    error,
    reload: () => setTick((t) => t + 1),
    setRows
  };
}

export interface WorkItemsTableTabProps {
  filters: WorkItemFilters;
  groupBy: GroupBy;
  urlAdapter: TableUrlStateAdapter;
  visibleCols: ReadonlyArray<string>;
  activeOrgId: string | null;
  /** When set, scope the table to a single project's work items. */
  projectId?: string;
  epicChoices: ReadonlyArray<{ id: string; name: string }>;
  onToast: (message: string | null) => void;
  onOpenItem: (id: string) => void;
  reloadSignal: number;
  /** Test-id prefix for the table + responsive card list (e.g. "work-items-route-table"). */
  testIdPrefix: string;
}

export function WorkItemsTableTab({
  filters,
  groupBy,
  urlAdapter,
  visibleCols,
  activeOrgId,
  projectId,
  epicChoices,
  onToast,
  onOpenItem,
  reloadSignal,
  testIdPrefix
}: WorkItemsTableTabProps) {
  const { state, rows, profilesById, error, reload, setRows } =
    useWorkItems(activeOrgId, projectId);

  // Refetch in place when the cover closes (reloadSignal starts at 0).
  useEffect(() => {
    if (reloadSignal > 0) reload();
  }, [reloadSignal, reload]);

  const filtered = useMemo(
    () => applyWorkItemFilters(rows, filters),
    [rows, filters]
  );

  const parentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) map.set(r.id, r.name);
    return map;
  }, [rows]);

  // When groupBy=epic, sort rows by (parent_work_item_id, name) so all of an
  // Epic's children land contiguously. The Epic itself sorts under its own id
  // (which is also its `parent_work_item_id` group key for visual grouping)
  // — practically this means Epics rise to the top of their group since no
  // child shares the Epic's id.
  const sortedForTable = useMemo(() => {
    if (groupBy !== 'epic') return filtered;
    const groupKey = (r: WorkItemRow) =>
      r.type === 'epic' ? r.id : r.parent_work_item_id ?? '~unassigned';
    return [...filtered].sort((a, b) => {
      const ag = groupKey(a);
      const bg = groupKey(b);
      if (ag !== bg) return ag.localeCompare(bg);
      // Epic itself first within its own group, then children by name.
      if (a.type === 'epic' && b.type !== 'epic') return -1;
      if (b.type === 'epic' && a.type !== 'epic') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered, groupBy]);

  const tableRows: TableRow[] = useMemo(
    () => sortedForTable.map((r) => toCellRow(r, profilesById)),
    [sortedForTable, profilesById]
  );

  const bulkActions: BulkAction[] = useMemo(
    () => STATUS_CHOICES.map((s) => ({ id: `status:${s}`, label: `→ ${s}` })),
    []
  );

  const onBulkAction = useCallback(
    async (ids: string[], action: BulkAction) => {
      if (!action.id.startsWith('status:')) return;
      const status = action.id.slice('status:'.length);
      const results = await Promise.all(
        ids.map((id) =>
          supabase.rpc('set_work_item_status', {
            p_work_item_id: id,
            p_status: status
          })
        )
      );
      const upErr = results.find((r) => r.error)?.error;
      if (upErr) {
        // eslint-disable-next-line no-console
        console.warn('[work-items] bulk status update failed:', upErr.message);
      }
      reload();
    },
    [reload]
  );

  // Compose the runtime columns from the static skeleton + async-loaded
  // editor options. Status / Priority are hardcoded enums; Epic comes from the
  // org-wide one-shot query; Assignee comes from the list_org_members roster.
  const runtimeColumns = useMemo<Column[]>(() => {
    const assigneeOpts = Array.from(profilesById, ([id, name]) => ({
      value: id,
      label: name
    }));
    const epicOpts = [
      { value: '', label: '(unassigned)' },
      ...epicChoices.map((e) => ({ value: e.id, label: e.name }))
    ];
    return WORK_ITEM_COLUMNS.map((c) => {
      if (c.key === 'status') {
        return {
          ...c,
          type: 'single_select',
          options: STATUS_CHOICES.map((s) => ({ value: s, label: s }))
        };
      }
      if (c.key === 'assignee') {
        return { ...c, type: 'person', options: assigneeOpts };
      }
      if (c.key === 'priority') {
        return {
          ...c,
          type: 'single_select',
          options: [{ value: '', label: '(none)' }, ...PRIORITY_CHOICES]
        };
      }
      if (c.key === 'epic') {
        return { ...c, type: 'single_select', options: epicOpts };
      }
      if (c.key === 'due') {
        return { ...c, type: 'date' };
      }
      return c;
    });
  }, [profilesById, epicChoices]);

  const handleCellEdit = useCallback(
    async (rowId: string, columnKey: string, newValue: CellValue) => {
      const row = rows.find((r) => r.id === rowId);
      if (!row) return;

      // Epic-row guards — per ux.md / web-mock prd.md, an Epic row cannot be
      // assigned to another Epic, and its status is managed from its detail
      // page (Epic statuses use the Epic-specific pipeline, not the shared
      // Backlog/In progress/Review/Done columns).
      if (columnKey === 'epic' && row.type === 'epic') {
        onToast("Epics can't be assigned to another Epic");
        return;
      }
      if (columnKey === 'status' && row.type === 'epic') {
        onToast("Use the Epic's detail page to change its status");
        return;
      }

      let patch: Record<string, string | null> | null = null;
      let fieldLabel = columnKey;
      if (columnKey === 'status' && typeof newValue === 'string') {
        patch = { status: newValue };
        fieldLabel = 'Status';
      } else if (columnKey === 'assignee') {
        const next =
          typeof newValue === 'string' && newValue.length > 0 ? newValue : null;
        patch = { assignee_id: next };
        fieldLabel = 'Assignee';
      } else if (columnKey === 'priority') {
        const next =
          typeof newValue === 'string' && newValue.length > 0 ? newValue : null;
        patch = { priority: next };
        fieldLabel = 'Priority';
      } else if (columnKey === 'due') {
        const next =
          typeof newValue === 'string' && newValue.length > 0 ? newValue : null;
        patch = { due_date: next };
        fieldLabel = 'Due date';
      } else if (columnKey === 'epic') {
        const next =
          typeof newValue === 'string' && newValue.length > 0 ? newValue : null;
        patch = { parent_work_item_id: next };
        fieldLabel = 'Epic';
      } else {
        return;
      }

      // Optimistic update — apply the patch locally so the cell re-renders
      // before the network round-trip resolves.
      const snapshot = rows;
      const optimistic = rows.map((r) =>
        r.id === rowId ? ({ ...r, ...patch } as WorkItemRow) : r
      );
      setRows(optimistic);

      // Route by `columnKey` — each editable cell maps to its own RPC. The
      // generated arg types are non-null `string`, so the null-clearing cases
      // cast `null` through `unknown` (same pattern as ChildTasksPanel's
      // set_work_item_parent unlink).
      let upErr: { message?: string } | null = null;
      if (columnKey === 'status' && typeof newValue === 'string') {
        ({ error: upErr } = await supabase.rpc('set_work_item_status', {
          p_work_item_id: rowId,
          p_status: newValue
        }));
      } else if (columnKey === 'assignee') {
        ({ error: upErr } = await supabase.rpc('set_work_item_assignee', {
          p_work_item_id: rowId,
          p_assignee_id:
            typeof newValue === 'string' && newValue.length > 0
              ? newValue
              : (null as unknown as string)
        }));
      } else if (columnKey === 'priority') {
        ({ error: upErr } = await supabase.rpc('set_work_item_priority', {
          p_work_item_id: rowId,
          p_priority:
            typeof newValue === 'string' && newValue.length > 0
              ? newValue
              : (null as unknown as string)
        }));
      } else if (columnKey === 'due') {
        const nextDue =
          typeof newValue === 'string' && newValue.length > 0
            ? newValue
            : (null as unknown as string);
        ({ error: upErr } = await supabase.rpc('set_work_item_dates', {
          p_work_item_id: rowId,
          p_start_date: (row.start_date ?? null) as unknown as string,
          p_end_date: (row.end_date ?? null) as unknown as string,
          p_due_date: nextDue
        }));
      } else if (columnKey === 'epic') {
        ({ error: upErr } = await supabase.rpc('set_work_item_parent', {
          p_work_item_id: rowId,
          p_parent_id:
            typeof newValue === 'string' && newValue.length > 0
              ? newValue
              : (null as unknown as string)
        }));
      }
      if (upErr) {
        setRows(snapshot);
        onToast(`Couldn't save ${fieldLabel} — try again`);
        return;
      }
      // Re-fetch so server-derived fields (updated_at, etc.) stay in sync.
      reload();
    },
    [rows, setRows, onToast, reload]
  );

  // Force-remount Table when the visible-cols set changes so Table re-reads
  // its `urlState.read()` seed (Table only consults it on first mount).
  const tableKey = visibleCols.join(',');

  // Chip set for the responsive card fallback. Reuses BOARD_CHIP_KEYS so the
  // same Properties picker (mode="columns") controls table columns AND the
  // properties shown on each mobile card — one URL param, one mental model.
  const cardChipKeys = useMemo(
    () => BOARD_CHIP_KEYS.filter((k) => visibleCols.includes(k)),
    [visibleCols]
  );

  return (
    <div
      className="work-items-route__table-wrap"
      data-testid={testIdPrefix}
      data-state={state}
      data-row-count={tableRows.length}
      data-visible-cols={visibleCols.join(',')}
    >
      {state === 'error' ? (
        <div className="work-items-route__error" role="alert">
          Failed to load work items: {error}
        </div>
      ) : null}
      <Table
        key={tableKey}
        columns={runtimeColumns}
        rows={tableRows}
        defaultSort={TABLE_DEFAULT_SORT}
        onCellEdit={handleCellEdit}
        onBulkAction={onBulkAction}
        bulkActions={bulkActions}
        multiSelectEnabled
        urlState={urlAdapter}
        dataTestId={testIdPrefix}
        onRowOpen={(id) => onOpenItem(id)}
      />
      <RecordCardList
        className="work-items-route__table-cards"
        data-testid={`${testIdPrefix}-cards`}
        data-visible-chips={cardChipKeys.join(',')}
      >
        {sortedForTable.map((row) => (
          <WorkItemPropertyCard
            key={row.id}
            row={row}
            parentName={
              row.parent_work_item_id
                ? parentNameById.get(row.parent_work_item_id) ?? null
                : null
            }
            assigneeName={
              row.assignee_id ? profilesById.get(row.assignee_id) ?? null : null
            }
            chipKeys={cardChipKeys}
            onOpen={() => onOpenItem(row.id)}
            onParentOpen={(parentId) => onOpenItem(parentId)}
            testIdPrefix={`${testIdPrefix}-card`}
          />
        ))}
      </RecordCardList>
    </div>
  );
}

function WorkItemPropertyCard({
  row,
  parentName,
  assigneeName,
  chipKeys,
  onOpen,
  onParentOpen,
  testIdPrefix
}: {
  row: WorkItemRow;
  parentName: string | null;
  assigneeName: string | null;
  chipKeys: ReadonlyArray<string>;
  onOpen: () => void;
  onParentOpen: (parentId: string) => void;
  testIdPrefix: string;
}) {
  const parentId = row.parent_work_item_id;
  const chips = chipKeys
    .map((k) => ({
      key: k,
      value: chipValueFor(k, row, parentName, assigneeName),
      isEpicLink: k === 'epic' && !!parentId,
      parentId
    }))
    .filter((c) => c.value != null);

  return (
    <RecordCard
      layout="stack"
      interactive
      onActivate={onOpen}
      data-testid={`${testIdPrefix}-${row.id}`}
      data-card-id={row.id}
      data-status={row.status}
    >
      <RecordCard.Title>{row.name}</RecordCard.Title>
      <WorkItemCardChips
        chips={chips}
        cardId={row.id}
        testIdPrefix={testIdPrefix}
        onParentOpen={onParentOpen}
      />
    </RecordCard>
  );
}

const EMPTY_CELL = '—';

function toCellRow(
  r: WorkItemRow,
  profilesById: ReadonlyMap<string, string>
): TableRow {
  const createdByName = r.created_by
    ? profilesById.get(r.created_by) ?? EMPTY_CELL
    : EMPTY_CELL;
  // For editable cells, the `value` we ship to <Table> is the raw id (so the
  // CellEditor's <select> finds its `value=` in `options`); the rendered text
  // for non-edit display falls back to the resolved label via formatCell's
  // option lookup in Table.tsx. Empty string lets the editor open on the
  // "(unassigned)" / "(none)" placeholder option.
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    status: r.status,
    assignee: r.assignee_id ?? '',
    priority: r.priority ?? '',
    due: r.due_date ?? '',
    last_event_at: r.updated_at ? formatDateShort(r.updated_at) : EMPTY_CELL,
    epic: r.type === 'epic' ? '' : r.parent_work_item_id ?? '',
    created_by: createdByName,
    created_at: r.created_at ? formatDateShort(r.created_at) : EMPTY_CELL,
    rank: r.rank
  };
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}
